import { Text } from '@truepill/react-capsule'
import { useQuery } from '@truepill/tpos-react-router'
import HyphenatedNdc from 'components/HyphenatedNdc'
import { GET_CRITICAL_MEDICAL_NOTE_BY_NDC, GET_CRITICAL_MEDICAL_NOTE_BY_GPI, GET_DRUG_DETAILS } from 'gql'
import { processPackage } from 'pages/PharmacyPage/PharmacyDrugPage/util'
import type { CriticalMedicalNotes, GetDrugDetailsQuery } from 'types'
import { MedicalNoteAlert } from './MedicalNoteAlert'

type CriticalMedicalNoteWarningProps = {
  ndc: string
  disableBackgroundColor?: boolean
  style?: React.CSSProperties
}

const CriticalMedicalNoteWarning = (props: CriticalMedicalNoteWarningProps): JSX.Element => {
  const { ndc, disableBackgroundColor, style } = props

  const {
    data: dataGetNdcCmn = { getNoteByNdc: undefined },
    loading: loadingGetNdcCmn,
    error: errorGetNdcCmn,
  } = useQuery<{
    getNoteByNdc: CriticalMedicalNotes
  }>(GET_CRITICAL_MEDICAL_NOTE_BY_NDC, {
    variables: { ndc },
  })

  // Query drug details to get GPI information
  const {
    data: drugDetailsData,
    loading: loadingDrugDetails,
    error: errorDrugDetails,
  } = useQuery<GetDrugDetailsQuery>(GET_DRUG_DETAILS, {
    variables: { ndc },
    skip: !!dataGetNdcCmn?.getNoteByNdc?.note, // Skip if NDC note exists
  })

  const packages = drugDetailsData?.getSingleNdc?.packages
  const pkg = processPackage(ndc, packages)
  const gpi = pkg?.gpi

  const {
    data: dataGetGpiCmn = { getNoteByGpi: undefined },
    loading: loadingGetGpiCmn,
    error: errorGetGpiCmn,
  } = useQuery<{
    getNoteByGpi: CriticalMedicalNotes
  }>(GET_CRITICAL_MEDICAL_NOTE_BY_GPI, {
    variables: { gpi: gpi || '' },
    skip: !!dataGetNdcCmn?.getNoteByNdc?.note || !gpi,
  })

  if (errorGetNdcCmn || errorGetGpiCmn || errorDrugDetails) {
    return (
      <Text>
        Error retrieving the critical medical notes for NDC <HyphenatedNdc ndc={ndc} />
      </Text>
    )
  }

  if (loadingGetNdcCmn || loadingGetGpiCmn || loadingDrugDetails) {
    return <></>
  }

  const effectiveCritMedNotes = dataGetNdcCmn?.getNoteByNdc?.note || dataGetGpiCmn?.getNoteByGpi?.note

  if (!effectiveCritMedNotes) {
    return <></>
  }

  return <MedicalNoteAlert note={effectiveCritMedNotes} disableBackgroundColor={disableBackgroundColor} style={style} />
}

export default CriticalMedicalNoteWarning
